import React, { useMemo, useState, useEffect } from 'react';
// FIX: Use namespace import for react-router-dom to fix potential module resolution errors.
import * as ReactRouterDom from 'react-router-dom';
import { useProjects } from '../hooks/useProjects';
import { useCompanies } from '../hooks/useCompanies';
import { COST_CATEGORIES, API_BASE_URL } from '../constants';
import { MainCostCategory, PieChartSegment, Project, Company, User, ExpenseEntry, ReportSubItem, ReportCategoryGroup } from '../types';
import { Button } from '../components/ui/Button';
import { Select } from '../components/ui/Select';
import { ArrowLeftIcon } from '../components/icons/ArrowLeftIcon';
import { PrinterIcon } from '../components/icons/PrinterIcon';
import { useNotifications } from '../hooks/useNotifications';
import { useAuth } from '../hooks/useAuth';
import { PrintableReport } from '../components/PrintableReport';
import { NotificationBell } from '../components/NotificationBell';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

// Types for the processed report data
// (Moved to types.ts)

interface ReportState {
    data: ReportCategoryGroup[];
    pieData: PieChartSegment[];
    grandTotal: number;
    period: { month: string; year: string };
    detailedExpenses?: ExpenseEntry[];
    isDetailedReport?: boolean;
    detailedReportTitle?: string;
}

const APP_VERSION = "v1.4.7 - Memória de Período";
const EXPECTED_SERVER_VERSION = "1.4.5";

// Helper function to get the correct abbreviated label for the pie chart
const getPieChartLabel = (id: MainCostCategory): string => {
    const category = COST_CATEGORIES.find(c => c.id === id);
    if (!category) return 'Outros';

    switch (id) {
        case MainCostCategory.DESPESAS_ADM:
            return 'Desp Adm';
        case MainCostCategory.TRIBUTOS_INSS:
            return 'INSS';
        default:
            // This will return 'CDO', 'CIO', 'Tributos' from the full name
            return category.name.split(' - ')[0];
    }
};

const RelatorioPage: React.FC = () => {
    const { obraId } = ReactRouterDom.useParams<{ obraId: string }>();
    const navigate = ReactRouterDom.useNavigate();
    const { projects } = useProjects();
    const { companies } = useCompanies();
    const { addNotification } = useNotifications();
    const { user } = useAuth();

    const today = new Date();
    const [selectedMonth, setSelectedMonth] = useState<string>(
        () => localStorage.getItem('lam-custos-report-month') || (today.getMonth() + 1).toString()
    );
    const [selectedYear, setSelectedYear] = useState<string>(
        () => localStorage.getItem('lam-custos-report-year') || today.getFullYear().toString()
    );
    
    // State for new filters
    const [isFilteringIndividual, setIsFilteringIndividual] = useState(false);
    const [selectedMainCategory, setSelectedMainCategory] = useState('');
    const [selectedSubItem, setSelectedSubItem] = useState('');

    const [report, setReport] = useState<ReportState | null>(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);

    useEffect(() => {
        try {
            localStorage.setItem('lam-custos-report-month', selectedMonth);
        } catch (e) {
            console.error("Failed to save report month to localStorage", e);
        }
    }, [selectedMonth]);

    useEffect(() => {
        try {
            localStorage.setItem('lam-custos-report-year', selectedYear);
        } catch (e) {
            console.error("Failed to save report year to localStorage", e);
        }
    }, [selectedYear]);

    const project = useMemo(() => projects.find(p => p.id === obraId), [projects, obraId]);
    const company = useMemo(() => project ? companies.find(c => c.id === project.companyId) : undefined, [project, companies]);

    const { monthOptions, yearOptions } = useMemo(() => {
        const months = [
            { value: '1', label: 'Janeiro' }, { value: '2', label: 'Fevereiro' },
            { value: '3', label: 'Março' }, { value: '4', label: 'Abril' },
            { value: '5', label: 'Maio' }, { value: '6', label: 'Junho' },
            { value: '7', label: 'Julho' }, { value: '8', label: 'Agosto' },
            { value: '9', 'label': 'Setembro' }, { value: '10', 'label': 'Outubro' },
            { value: '11', 'label': 'Novembro' }, { value: '12', 'label': 'Dezembro' },
        ];
        const currentYear = new Date().getFullYear();
        const years = Array.from({ length: 6 }, (_, i) => currentYear - i); // Current year + last 5
        const yearOpts = years.map(y => ({ value: y.toString(), label: y.toString() }));

        return { monthOptions: months, yearOptions: yearOpts };
    }, []);

    // Options for the new filter dropdowns
    const mainCategoryOptions = useMemo(() => 
        COST_CATEGORIES.map(cat => ({
            value: cat.id,
            label: cat.name
        })), []);

    const subItemOptions = useMemo(() => {
        if (!selectedMainCategory) return [];
        const category = COST_CATEGORIES.find(c => c.id === selectedMainCategory);
        if (!category) return [];
        return category.subItems.map(item => ({
            value: item.id,
            label: item.name,
        }));
    }, [selectedMainCategory]);

    const handleFilterToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
        const isChecked = e.target.checked;
        setIsFilteringIndividual(isChecked);
        if (!isChecked) {
            setSelectedMainCategory('');
            setSelectedSubItem('');
        }
    };

    const handleGenerateReport = async () => {
        if (!project || !company || !obraId) return;
        setIsGenerating(true);
        setReport(null); // Clear previous report

        try {
            const diagResponse = await fetch(`${API_BASE_URL}/diagnostics`);
            if (!diagResponse.ok) {
                throw new Error('Não foi possível verificar a versão do servidor. O backend pode estar offline.');
            }
            const diagData = await diagResponse.json();
            if (diagData.version !== EXPECTED_SERVER_VERSION) {
                throw new Error(`ERRO DE DEPLOY: A versão do seu backend (${diagData.version || 'desconhecida'}) é incompatível com a do frontend (que requer v${EXPECTED_SERVER_VERSION}). Isso significa que a atualização no servidor falhou. Siga o procedimento de reinicialização total no terminal.`);
            }

            const params = new URLSearchParams({
                projectId: obraId,
                year: selectedYear,
                month: selectedMonth,
            });
            const url = `${API_BASE_URL}/expenses/report?${params.toString()}`;

            const response = await fetch(url);
            
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ message: `Erro ${response.status}: ${response.statusText}`}));
                throw new Error(errorData.message || 'Falha ao buscar os dados do relatório do servidor.');
            }
            const fetchedExpenses: ExpenseEntry[] = await response.json();
            
            // --- Detailed Report Logic ---
            if (isFilteringIndividual && selectedMainCategory && selectedSubItem) {
                const expensesToProcess = fetchedExpenses.filter(expense => {
                    const categoryInfo = COST_CATEGORIES.find(c => c.id === expense.mainCategory?.toLowerCase() || c.name === expense.mainCategory);
                    return categoryInfo?.id === selectedMainCategory && expense.subCategoryItemId === selectedSubItem;
                });
                
                const totalGeral = expensesToProcess.reduce((sum, exp) => sum + (Number(exp.amount) || 0), 0);
                const mainCatName = mainCategoryOptions.find(o => o.value === selectedMainCategory)?.label;
                const subItemName = subItemOptions.find(o => o.value === selectedSubItem)?.label;

                setReport({
                    data: [],
                    pieData: [],
                    grandTotal: totalGeral,
                    period: { month: monthOptions.find(m => m.value === selectedMonth)!.label, year: selectedYear },
                    detailedExpenses: expensesToProcess.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()),
                    isDetailedReport: true,
                    detailedReportTitle: `${mainCatName} - ${subItemName}`,
                });
                return; // End execution here for detailed report
            }
            
            // --- Summary Report Logic (existing logic) ---
            const expensesToProcess = fetchedExpenses;
            
            // Check for missing entries ONLY IF we are not filtering individually
            const missingEntries: { categoryName: string; subItemName: string }[] = [];
            COST_CATEGORIES.forEach(categoryInfo => {
                if (categoryInfo.id === MainCostCategory.CDO || categoryInfo.id === MainCostCategory.CIO) {
                    categoryInfo.subItems.forEach(subItem => {
                        const hasEntry = expensesToProcess.some(exp => exp.subCategoryItemId === subItem.id);
                        if (!hasEntry) {
                            missingEntries.push({ categoryName: categoryInfo.name, subItemName: subItem.name });
                        }
                    });
                }
            });

            if (missingEntries.length > 0) {
                const projectLocation = company ? `${company.address.city}, ${company.address.state}` : 'Local não definido';
                addNotification({
                    type: 'missing_entries',
                    message: `Lançamentos pendentes para ${project.name}.`,
                    payload: {
                        items: missingEntries.map(me => `${me.categoryName.split(' - ')[0]}: ${me.subItemName}`),
                        projectName: project.name,
                        projectLocation: projectLocation
                    }
                });
            }
            
            // Aggregate data for report using the processed list of expenses
            const aggregatedData: { [key in MainCostCategory]?: { [key: string]: { name: string; total: number } } } = {};
            expensesToProcess.forEach(expense => {
                const categoryInfo = COST_CATEGORIES.find(c => c.id === expense.mainCategory?.toLowerCase() || c.name === expense.mainCategory);
                const categoryId = categoryInfo?.id;
                
                if (!categoryId) return;

                if (!aggregatedData[categoryId]) aggregatedData[categoryId] = {};
                if (!aggregatedData[categoryId]![expense.subCategoryItemId]) {
                    aggregatedData[categoryId]![expense.subCategoryItemId] = { name: expense.subCategoryItemName, total: 0 };
                }
                aggregatedData[categoryId]![expense.subCategoryItemId].total += (Number(expense.amount) || 0);
            });

            const finalReport: ReportCategoryGroup[] = COST_CATEGORIES.map(categoryInfo => {
                const catData = aggregatedData[categoryInfo.id];
                if (!catData) return null;

                const subItems = Object.values(catData).sort((a,b) => a.name.localeCompare(b.name));
                const total = subItems.reduce((sum, item) => sum + item.total, 0);

                if (total === 0) return null;

                return {
                    id: categoryInfo.id,
                    name: categoryInfo.name.split(' - ')[1] || categoryInfo.name,
                    total: total,
                    subItems: subItems,
                }
            }).filter((item): item is ReportCategoryGroup => item !== null);

            const totalGeral = finalReport.reduce((sum, cat) => sum + cat.total, 0);
            
            const colors: { [key in MainCostCategory]?: string } = {
                [MainCostCategory.CDO]: '#FF6384', [MainCostCategory.CIO]: '#36A2EB', [MainCostCategory.TRIBUTOS_INSS]: '#FFCE56',
                [MainCostCategory.DESPESAS_ADM]: '#4BC0C0', [MainCostCategory.TRIBUTOS]: '#9966FF',
            };
            const pieData = finalReport.map(cat => ({
                name: getPieChartLabel(cat.id),
                value: cat.total,
                percentage: totalGeral > 0 ? (cat.total / totalGeral) * 100 : 0,
                color: colors[cat.id] || '#CCCCCC',
            }));

            setReport({
                data: finalReport,
                pieData: pieData,
                grandTotal: totalGeral,
                period: { month: monthOptions.find(m => m.value === selectedMonth)!.label, year: selectedYear },
                isDetailedReport: false,
                detailedExpenses: undefined
            });

        } catch (error) {
            console.error("Failed to generate report:", error);
            alert(`Erro ao gerar relatório: ${(error as Error).message}`);
        } finally {
            setIsGenerating(false);
        }
    };

    const handleDownloadPdf = async () => {
        if (!report || isDownloading) return;

        const reportElement = document.getElementById('report-content');
        if (!reportElement) {
            alert("Elemento do relatório não encontrado para gerar o PDF.");
            return;
        }

        setIsDownloading(true);

        try {
            const canvas = await html2canvas(reportElement, {
                scale: 1.5,
                useCORS: true,
                logging: false,
                backgroundColor: '#ffffff'
            });

            const imgData = canvas.toDataURL('image/jpeg', 0.85);
            const pdf = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: 'a4'
            });

            const A4_PAGE_WIDTH_MM = pdf.internal.pageSize.getWidth();
            const A4_PAGE_HEIGHT_MM = pdf.internal.pageSize.getHeight();
            const MARGIN_MM = 10;

            const contentWidthMm = A4_PAGE_WIDTH_MM - (MARGIN_MM * 2);
            const contentHeightMm = (canvas.height * contentWidthMm) / canvas.width;

            let heightLeft = contentHeightMm;
            let position = MARGIN_MM;

            pdf.addImage(imgData, 'JPEG', MARGIN_MM, position, contentWidthMm, contentHeightMm);
            heightLeft -= (A4_PAGE_HEIGHT_MM - MARGIN_MM * 2);

            while (heightLeft > 0) {
                position -= A4_PAGE_HEIGHT_MM;
                pdf.addPage();
                pdf.addImage(imgData, 'JPEG', MARGIN_MM, position, contentWidthMm, contentHeightMm);
                heightLeft -= A4_PAGE_HEIGHT_MM;
            }

            const monthLabel = monthOptions.find(m => m.value === selectedMonth)?.label || "mes";
            const filename = `Relatorio_${project?.name.replace(/[^a-zA-Z0-9]/g, '_')}_${monthLabel}_${selectedYear}.pdf`;

            pdf.save(filename);

        } catch (error) {
            console.error("Erro ao gerar PDF:", error);
            alert("Ocorreu um erro ao tentar gerar o PDF. Por favor, tente novamente.");
        } finally {
            setIsDownloading(false);
        }
    };

    if (!project || !company || !user) {
        return <div className="text-center py-10">Dados da obra, empresa ou usuário não encontrados.</div>;
    }

    return (
        <div className="bg-white dark:bg-dark-bg text-gray-900 dark:text-dark-text-primary min-h-screen">
            <header className="print-hidden-section flex flex-col gap-4 p-4 bg-gray-200 dark:bg-dark-primary shadow-md relative">
                <div className="flex flex-wrap justify-between items-center gap-4">
                    <Button variant="secondary" onClick={() => navigate(`/obras/${obraId}`)} leftIcon={<ArrowLeftIcon className="text-xl"/>}>
                        Voltar
                    </Button>
                    <div className='flex items-center gap-2'>
                        <Select options={monthOptions} value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)} containerClassName="mb-0 w-36" className="bg-white dark:bg-dark-secondary dark:border-dark-tertiary"/>
                        <Select options={yearOptions} value={selectedYear} onChange={e => setSelectedYear(e.target.value)} containerClassName="mb-0 w-28" className="bg-white dark:bg-dark-secondary dark:border-dark-tertiary"/>
                        <Button variant="primary" onClick={handleGenerateReport} isLoading={isGenerating}>
                            {isGenerating ? 'Gerando...' : 'Gerar'}
                        </Button>
                        <NotificationBell />
                    </div>
                    <Button 
                        variant="primary" 
                        onClick={handleDownloadPdf} 
                        leftIcon={<PrinterIcon className="text-xl"/>} 
                        disabled={!report || (report.data.length === 0 && !report.isDetailedReport) || isDownloading}
                        isLoading={isDownloading}
                    >
                        {isDownloading ? 'Baixando...' : 'Baixar'}
                    </Button>
                </div>

                <div className="flex flex-row items-center gap-4 p-3 bg-gray-100 dark:bg-dark-secondary rounded-md border border-gray-300 dark:border-dark-tertiary w-full">
                    <div className="flex items-center flex-shrink-0">
                        <input 
                            type="checkbox" 
                            id="filterIndividual" 
                            checked={isFilteringIndividual} 
                            onChange={handleFilterToggle}
                            className="h-5 w-5 rounded border-gray-400 text-brand-teal focus:ring-brand-teal"
                        />
                        <label htmlFor="filterIndividual" className="ml-2 font-medium text-gray-700 dark:text-dark-text-secondary">
                            Custos Individuais
                        </label>
                    </div>

                    {isFilteringIndividual && (
                        <div className="flex items-center gap-2 flex-grow">
                            <Select 
                                options={mainCategoryOptions} 
                                value={selectedMainCategory} 
                                onChange={e => {
                                    setSelectedMainCategory(e.target.value);
                                    setSelectedSubItem('');
                                }} 
                                containerClassName="mb-0 flex-1" 
                                className="bg-gray-700 text-white border-gray-600"
                                placeholderOption="Selecione o Custo Individual"
                            />
                            <Select 
                                options={subItemOptions} 
                                value={selectedSubItem} 
                                onChange={e => setSelectedSubItem(e.target.value)} 
                                containerClassName="mb-0 flex-1" 
                                className="bg-gray-600 text-white border-gray-500"
                                placeholderOption="Selecione a Categoria"
                                disabled={!selectedMainCategory || subItemOptions.length === 0}
                            />
                        </div>
                    )}
                </div>

                 <p className="absolute bottom-1 right-2 text-xs text-gray-500">{APP_VERSION}</p>
            </header>

            <main className='p-4 md:p-6 lg:p-8 text-black dark:text-dark-text-primary'>
                {!report ? (
                    <div className='text-center py-24 text-gray-500'>
                        <p>Selecione o mês e o ano e clique em "Gerar" para visualizar o relatório.</p>
                    </div>
                ) : (report.data.length === 0 && (!report.detailedExpenses || report.detailedExpenses.length === 0)) ? (
                    <div className='text-center py-24 text-gray-500'>
                        <p>Nenhum lançamento encontrado para {report.period.month} de {report.period.year} com os filtros selecionados.</p>
                    </div>
                ) : (
                   <div className="bg-white rounded-md">
                        <PrintableReport 
                            project={project}
                            company={company}
                            user={user}
                            reportData={report.data}
                            pieChartData={report.pieData}
                            grandTotal={report.grandTotal}
                            reportPeriod={report.period}
                            version={APP_VERSION}
                            isDetailedReport={report.isDetailedReport}
                            detailedExpenses={report.detailedExpenses}
                            detailedReportTitle={report.detailedReportTitle}
                        />
                    </div>
                )}
            </main>
        </div>
    );
};

export default RelatorioPage;
